/**
 * Payoff Visualizer & Strike Manipulation Engine — Iron Condor anchor
 * strategy (Lead Quantitative Trading UI Engineer refactor). Backed by
 * `POST /strategy-adjustment/{symbol}/payoff-curve` (`PayoffCurveRead`) for
 * the T+0 Black-Scholes line and every backend-computed metric, PLUS a
 * pure, zero-latency client-side expiry-payoff calculator
 * (`payoffMath.ts`) for the solid "Expiry P&L" line — an at-expiry option
 * payoff is intrinsic value only, so it needs no Black-Scholes model and
 * can be redrawn on every mouse-move with no backend round-trip.
 *
 * SINGLE SOURCE OF TRUTH (requirement 1): both the numerical Trade Builder
 * table and this chart read/write the exact same `legs` prop. Dragging a
 * strike handle calls `onChangeLegs(...)` on every move — this instantly
 * updates the leg table (same array, same callback `TradeBuilderCard`
 * itself uses) and, via `StrategyAdjustmentPage`'s own existing debounced
 * effect, the heavy backend recalculation. There is no separate "drag
 * draft" state to keep in sync with anything.
 *
 * DEBOUNCE CONTRACT (requirement 5): `onDragActiveChange(true)` fires on
 * mousedown on a handle; the caller (`StrategyAdjustmentPage`) suppresses
 * its debounced backend refresh entirely while active, so a drag of any
 * length makes ZERO backend calls. `onDragActiveChange(false)` fires on
 * mouseup, letting that same debounce fire once, immediately after —
 * "full re-render on mouse release."
 *
 * STRIKE MANIPULATION is Iron-Condor-specific (`identifyCondorLegs()`):
 * exactly one PE/BUY, PE/SELL, CE/SELL, CE/BUY leg on one expiry. Every
 * other strategy shape (converted via "Convert Strategy", or a
 * calendar/diagonal spread) renders this exact same chart with NO drag
 * handles at all — completely unchanged, read-only behavior, so nothing
 * about the other 13 supported strategy shapes regresses.
 *
 * Chart.js + `react-chartjs-2` is this codebase's only installed charting
 * library (see `HistoryCharts.tsx`) — there is no `chartjs-plugin-
 * annotation` package, so the zero-P&L line, current-spot line, and
 * Breakeven/Max Profit/Max Loss markers are plain Chart.js datasets, and
 * the draggable strike handles + breakeven axis labels are a thin
 * absolutely-positioned HTML overlay kept in pixel-sync with the chart via
 * `chart.scales.x.getPixelForValue()`/`getValueForPixel()` — the standard
 * technique for adding custom interactivity to a Chart.js canvas.
 *
 * THEME NOTE: this page's existing UI (`StrategyAdjustmentPage.tsx`'s own
 * docstring: "Follows the existing TekInvest UI theme") is MUI light-theme
 * throughout, and every sibling card on this page (Trade Builder, Risk &
 * Greeks) is light. The line/fill colors below are chosen for strong
 * contrast on that existing light background rather than switching this
 * one card to a dark background, which would look inconsistent sitting
 * directly between two light cards on the same page.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Slider,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  Legend,
  type Chart as ChartInstance,
} from "chart.js";
import { Line } from "react-chartjs-2";
import type { OptionLeg, PayoffCurveRead, RecalculatedMetricsRead } from "@/types";
import { fmtCurrency, fmtNum } from "./constants";
import {
  applyCondorDragGuardrails,
  applyCondorStrikeChange,
  approximateDeltaAt,
  buildStaticStrikeGroups,
  CONDOR_ROLE_LABELS,
  CONDOR_ROLE_ORDER,
  type CondorRole,
  computeChartViewWindow,
  computeNetPremium,
  findLocalBreakevens,
  generateExpiryPayoffCurve,
  getStrikeInterval,
  identifyCondorLegs,
  type LocalPayoffPoint,
  type StaticStrikeGroup,
  widenWingsSymmetrically,
} from "./payoffMath";

ChartJS.register(LinearScale, PointElement, LineElement, ChartTooltip, Legend, Filler);

const COLOR_MAX_PROFIT = "#00B386";
const COLOR_MAX_LOSS = "#D32F2F";
const COLOR_BREAKEVEN = "#1976D2";
const COLOR_EXPIRY_LINE = "#0F3D63";
const COLOR_T0_LINE = "#FB8C00";
const COLOR_PROFIT_FILL = "rgba(0, 179, 134, 0.12)";
const COLOR_LOSS_FILL = "rgba(211, 47, 47, 0.12)";
const COLOR_ZERO_LINE = "#9E9E9E";
const COLOR_SPOT_LINE = "#6A1B9A";
const COLOR_HANDLE: Record<CondorRole, string> = {
  LONG_PUT: "#1976D2",
  SHORT_PUT: "#00B386",
  SHORT_CALL: "#00B386",
  LONG_CALL: "#1976D2",
};

const SPOT_MOVE_PRESETS = [-5, -2, -1, 1, 2, 5];

interface MarkerPoint {
  x: number;
  y: number;
  label: string;
  color: string;
}

function buildMarkers(points: LocalPayoffPoint[], metrics: RecalculatedMetricsRead | null): MarkerPoint[] {
  if (!metrics || points.length === 0) return [];
  const markers: MarkerPoint[] = [];

  if (metrics.max_profit != null) {
    const best = points.reduce((a, b) => (b.expiry_pnl > a.expiry_pnl ? b : a));
    markers.push({ x: best.underlying_price, y: metrics.max_profit, label: "Max Profit", color: COLOR_MAX_PROFIT });
  }
  if (metrics.max_risk != null) {
    const worst = points.reduce((a, b) => (b.expiry_pnl < a.expiry_pnl ? b : a));
    markers.push({ x: worst.underlying_price, y: -metrics.max_risk, label: "Max Loss", color: COLOR_MAX_LOSS });
  }
  for (const breakeven of metrics.breakeven_points) {
    markers.push({ x: breakeven, y: 0, label: "Breakeven", color: COLOR_BREAKEVEN });
  }
  return markers;
}

function nearestByPrice<T extends { underlying_price: number }>(points: T[], price: number): T | null {
  if (points.length === 0) return null;
  let best = points[0];
  let bestDist = Math.abs(points[0].underlying_price - price);
  for (const p of points) {
    const d = Math.abs(p.underlying_price - price);
    if (d < bestDist) {
      best = p;
      bestDist = d;
    }
  }
  return best;
}

interface HandleGeometry {
  role: CondorRole;
  strike: number;
  leftPx: number;
  topPx: number;
  bottomPx: number;
}

interface BreakevenLabelGeometry {
  value: number;
  leftPx: number;
  topPx: number;
}

interface StaticStrikeLabelGeometry extends StaticStrikeGroup {
  leftPx: number;
  topPx: number;
}

/** Minimum horizontal gap (px) enforced between adjacent overlay labels
 * (breakeven or static-strike) — the exact "labels collapse into an
 * unreadable smear" failure this view-window fix targets can still recur
 * in miniature at extreme zoom levels, so labels that would land closer
 * than this to the previous one are dropped rather than drawn on top of
 * each other (the reference line itself still renders either way). */
const MIN_LABEL_GAP_PX = 42;

function thinByPixelGap<T extends { leftPx: number }>(items: T[]): T[] {
  const sorted = [...items].sort((a, b) => a.leftPx - b.leftPx);
  const kept: T[] = [];
  for (const item of sorted) {
    if (kept.length === 0 || item.leftPx - kept[kept.length - 1].leftPx >= MIN_LABEL_GAP_PX) {
      kept.push(item);
    }
  }
  return kept;
}

export interface PayoffChartProps {
  /** Backend-authoritative curve (T+0 line + metadata) — refreshed by the
   * caller's own debounced effect, frozen while `onDragActiveChange(true)`
   * is in effect. */
  curve: PayoffCurveRead;
  metrics: RecalculatedMetricsRead | null;
  /** The SAME legs array `TradeBuilderCard` renders — required so this
   * component can (a) derive the zero-latency local Expiry P&L line and
   * (b) detect/drive the Iron Condor drag handles. */
  legs: OptionLeg[];
  onChangeLegs: (legs: OptionLeg[]) => void;
  symbolCode: string;
  liveUnderlyingPrice: number | null;
  underlyingPriceOverride: number | null;
  onChangeUnderlyingPriceOverride: (value: number | null) => void;
  /** Fires `true` on drag start / `false` on drag end — the caller must
   * suppress its own debounced backend refresh while `true` (see module
   * docstring's "DEBOUNCE CONTRACT"). */
  onDragActiveChange?: (active: boolean) => void;
}

export function PayoffChart({
  curve,
  metrics,
  legs,
  onChangeLegs,
  symbolCode,
  liveUnderlyingPrice,
  underlyingPriceOverride,
  onChangeUnderlyingPriceOverride,
  onDragActiveChange,
}: PayoffChartProps) {
  const titleId = useId();
  const chartRef = useRef<ChartInstance<"line"> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const legsRef = useRef(legs);
  legsRef.current = legs;
  const draggingRoleRef = useRef<CondorRole | null>(null);
  const rafRef = useRef<number | null>(null);

  const [draggingRole, setDraggingRole] = useState<CondorRole | null>(null);
  const [handles, setHandles] = useState<HandleGeometry[]>([]);
  const [breakevenLabels, setBreakevenLabels] = useState<BreakevenLabelGeometry[]>([]);
  const [staticStrikeLabels, setStaticStrikeLabels] = useState<StaticStrikeLabelGeometry[]>([]);

  const effectiveSpot = underlyingPriceOverride ?? liveUnderlyingPrice ?? curve.underlying_price ?? null;
  const condor = useMemo(() => identifyCondorLegs(legs), [legs]);
  const strikeInterval = useMemo(() => getStrikeInterval(symbolCode, legs), [symbolCode, legs]);

  // The solid Expiry P&L line is ALWAYS derived locally from `legs` — see
  // module docstring. Falls back to the backend curve's own expiry points
  // only if, for some reason, `legs` is empty (nothing to derive).
  const localExpiryPoints = useMemo(() => generateExpiryPayoffCurve(legs, effectiveSpot), [legs, effectiveSpot]);
  const expiryPoints: LocalPayoffPoint[] =
    localExpiryPoints.length > 0
      ? localExpiryPoints
      : curve.points.filter((p): p is typeof p & { expiry_pnl: number } => p.expiry_pnl != null);

  const localBreakevens = useMemo(() => findLocalBreakevens(expiryPoints), [expiryPoints]);
  const markers = useMemo(() => buildMarkers(expiryPoints, metrics), [expiryPoints, metrics]);
  const breakevenValues = metrics?.breakeven_points?.length ? metrics.breakeven_points : localBreakevens;

  // Non-Condor strategies get no draggable handles, so give them a static
  // (read-only) reference line + label per leg strike instead — otherwise
  // 13 of the 14 supported strategy shapes render a payoff curve with no
  // visual tie back to which x-position is which leg's strike at all.
  const staticStrikeGroups = useMemo(() => (condor ? [] : buildStaticStrikeGroups(legs)), [condor, legs]);

  const t0Data = useMemo(() => curve.points.map((p) => ({ x: p.underlying_price, y: p.t0_pnl })), [curve.points]);
  const expiryData = useMemo(
    () => expiryPoints.map((p) => ({ x: p.underlying_price, y: p.expiry_pnl })),
    [expiryPoints]
  );

  const { yPad, xMin, xMax } = useMemo(() => {
    const allY = [...expiryData.map((p) => p.y), ...t0Data.map((p) => p.y), 0];
    const allX = [...expiryData.map((p) => p.x), ...t0Data.map((p) => p.x)];
    return {
      yPad: Math.max(...allY.map(Math.abs), 1) * 1.15,
      xMin: allX.length ? Math.min(...allX) : 0,
      xMax: allX.length ? Math.max(...allX) : 1,
    };
  }, [expiryData, t0Data]);

  // The VISIBLE x-axis window — deliberately much tighter than [xMin, xMax]
  // (the full payoff-curve data grid, which spans 0 to 1.6x the highest
  // strike so its flat plateaus past the wings are captured exactly). An
  // axis auto-scaled to that full grid squeezes every strike, breakeven,
  // and the spot marker into a sliver a few pixels wide — see
  // `computeChartViewWindow`'s own docstring. Recomputes automatically as
  // legs/spot change, so the chart always keeps the trading-relevant
  // action centered and zoomed in, with no manual zoom control needed.
  const viewWindow = useMemo(() => {
    const result = computeChartViewWindow(legs, effectiveSpot, breakevenValues, strikeInterval, xMin, xMax);
    // DIAGNOSTIC (kept permanently, not DEV-only): every prior round of this
    // bug (sliver axis, then a 30,000+ blowout after Convert Strategy) was
    // only fully understood by reading actual pixels off a screenshot and
    // reverse-engineering the inputs. `computeChartViewWindow` is provably
    // bounded to `highest + (1.5 + MAX_SAFETY_WIDEN_WING_MULTIPLE) *
    // rightWingWidth` on the high side for any non-empty `legs` -- for a
    // well-formed condor that's nowhere near the raw data grid's own
    // `xMax`. So `result.max` landing at-or-past `xMax` (or `result.min`
    // at-or-before `xMin`) with legs present should be IMPOSSIBLE by the
    // math, and if it's ever observed again this log is the fastest way to
    // see the actual runtime `legs`/spot/breakevens that produced it,
    // straight from the browser console -- no screenshot round-trip needed.
    if (legs.length > 0 && (result.max >= xMax - 1e-6 || result.min <= xMin + 1e-6)) {
      console.warn("[PayoffChart] view window failed to tighten around the data grid -- please copy this object", {
        legs: legs.map((l) => ({ strike: l.strike, option_type: l.option_type, side: l.side, is_hedge: l.is_hedge })),
        effectiveSpot,
        breakevenValues,
        strikeInterval,
        dataMin: xMin,
        dataMax: xMax,
        computedViewWindow: result,
      });
    }
    return result;
  }, [legs, effectiveSpot, breakevenValues, strikeInterval, xMin, xMax]);

  // Force react-chartjs-2/Chart.js to fully tear down and recreate the
  // underlying chart instance (rather than mutate it in place) whenever the
  // SETTLED view window changes -- i.e. every time except mid-drag, when
  // `viewWindow` can tick every mousemove and a remount would be janky.
  // This exists because every static-analysis pass on this bug (see the
  // diagnostic above) proves `computeChartViewWindow`'s output is correctly
  // bounded, yet the LIVE chart has repeatedly rendered a far wider axis
  // than that provably-correct output -- pointing at Chart.js's own
  // in-place scale-update path, not this file's math, as the remaining
  // suspect. A `key` change is React's standard way to sidestep exactly
  // that class of "a library's internal state doesn't fully re-sync with
  // new props" bug: a fresh Chart.js instance is built from scratch with
  // the new min/max baked in from creation, with nothing stale to carry
  // over.
  const chartKey = draggingRole ? "dragging" : `${Math.round(viewWindow.min)}-${Math.round(viewWindow.max)}`;

  // BULLETPROOFING (belt-and-suspenders on top of the `key`-remount fix
  // above): `scales.x.min`/`max` alone should be sufficient to clip the
  // visible axis to `viewWindow`, but every raw dataset (`expiryData`,
  // `t0Data`, the Zero P&L anchor line, Key Levels markers) still carries
  // points all the way out to the full payoff-curve data grid (`xMin`/
  // `xMax`, 0 to 1.6x the highest strike). If Chart.js's own axis-min/max
  // handling is EVER unreliable for any reason, its internal auto-scale
  // fallback has no data past this margin to expand into, so the rendered
  // axis physically cannot blow back out to the raw grid's edge again. A
  // small margin (rather than an exact `viewWindow` clip) keeps the curve
  // drawing a few points past the visible edge so it doesn't look
  // artificially truncated right at the axis boundary.
  const VIEW_WINDOW_DATA_MARGIN = Math.max(strikeInterval * 2, 200);
  const inWindow = useCallback(
    (x: number) => x >= viewWindow.min - VIEW_WINDOW_DATA_MARGIN && x <= viewWindow.max + VIEW_WINDOW_DATA_MARGIN,
    [viewWindow, VIEW_WINDOW_DATA_MARGIN]
  );
  const filteredExpiryData = useMemo(() => expiryData.filter((p) => inWindow(p.x)), [expiryData, inWindow]);
  const filteredT0Data = useMemo(() => t0Data.filter((p) => inWindow(p.x)), [t0Data, inWindow]);
  const filteredMarkers = useMemo(() => markers.filter((m) => inWindow(m.x)), [markers, inWindow]);
  const zeroLineX0 = Math.max(xMin, viewWindow.min - VIEW_WINDOW_DATA_MARGIN);
  const zeroLineX1 = Math.min(xMax, viewWindow.max + VIEW_WINDOW_DATA_MARGIN);

  /* eslint-disable @typescript-eslint/no-explicit-any -- Chart.js's
   * scriptable-option types (segment callbacks, tooltip callbacks,
   * string-literal interaction modes) are stricter than this file needs;
   * the existing PayoffChart.tsx already cast both `data`/`options` to
   * `any` at the `<Line>` call site for the same reason. Disabled for the
   * whole `data`/`options` construction block below rather than per line,
   * since the scriptable-option callbacks are nested several lines under
   * each object literal. Re-enabled immediately after `options`. */
  const data: any = useMemo(
    () => ({
    datasets: [
      {
        label: "Expiry P&L",
        data: filteredExpiryData,
        borderColor: COLOR_EXPIRY_LINE,
        backgroundColor: (ctx: any) => {
          const raw = ctx.raw as { y: number } | undefined;
          return (raw?.y ?? 0) >= 0 ? COLOR_PROFIT_FILL : COLOR_LOSS_FILL;
        },
        segment: {
          backgroundColor: (ctx: any) => {
            const avg = ((ctx.p0.parsed.y as number) + (ctx.p1.parsed.y as number)) / 2;
            return avg >= 0 ? COLOR_PROFIT_FILL : COLOR_LOSS_FILL;
          },
        },
        fill: "origin",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0,
        order: 3,
      },
      {
        label: "T+0 Expected P&L",
        data: filteredT0Data,
        borderColor: COLOR_T0_LINE,
        backgroundColor: COLOR_T0_LINE,
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1,
        order: 2,
      },
      {
        label: "Zero P&L",
        data: [
          { x: zeroLineX0, y: 0 },
          { x: zeroLineX1, y: 0 },
        ],
        borderColor: COLOR_ZERO_LINE,
        borderWidth: 1,
        borderDash: [3, 3],
        pointRadius: 0,
        fill: false,
        order: 1,
      },
      ...(effectiveSpot != null
        ? [
            {
              label: "Current Spot",
              data: [
                { x: effectiveSpot, y: -yPad },
                { x: effectiveSpot, y: yPad },
              ],
              borderColor: COLOR_SPOT_LINE,
              borderWidth: 1.5,
              borderDash: [2, 2],
              pointRadius: 0,
              fill: false,
              order: 1,
            },
          ]
        : []),
      ...(filteredMarkers.length > 0
        ? [
            {
              label: "Key Levels",
              data: filteredMarkers,
              showLine: false,
              pointRadius: 5,
              pointHoverRadius: 7,
              pointBackgroundColor: filteredMarkers.map((m) => m.color),
              pointBorderColor: "#fff",
              pointBorderWidth: 1,
              order: 0,
            },
          ]
        : []),
      ...staticStrikeGroups.map((group) => ({
        label: `Strike ${group.strike}`,
        data: [
          { x: group.strike, y: -yPad },
          { x: group.strike, y: yPad },
        ],
        borderColor: group.color,
        borderWidth: 1,
        borderDash: [3, 3],
        pointRadius: 0,
        fill: false,
        order: 1,
      })),
    ],
    }),
    [filteredExpiryData, filteredT0Data, zeroLineX0, zeroLineX1, yPad, effectiveSpot, filteredMarkers, staticStrikeGroups]
  );

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    animation: draggingRole ? false : undefined,
    interaction: { mode: "x", intersect: false },
    onResize: () => scheduleGeometryUpdate(),
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          font: { size: 11 },
          filter: (item: any) =>
            item.text !== "Zero P&L" && item.text !== "Current Spot" && !item.text.startsWith("Strike "),
        },
      },
      tooltip: {
        enabled: draggingRole === null,
        callbacks: {
          title: (items: any[]) => (items[0] ? `Underlying: ${fmtNum(items[0].parsed.x, 0)}` : ""),
          label: (item: any) => {
            if (item.dataset.label === "Key Levels") {
              const marker = markers[item.dataIndex];
              return `${marker.label}: ${fmtCurrency(marker.y)}`;
            }
            if (
              item.dataset.label === "Zero P&L" ||
              item.dataset.label === "Current Spot" ||
              item.dataset.label.startsWith("Strike ")
            )
              return "";
            return `${item.dataset.label}: ${fmtCurrency(item.parsed.y)}`;
          },
          afterBody: (items: any[]) => {
            const priceItem = items.find((i) => i.dataset.label === "Expiry P&L" || i.dataset.label === "T+0 Expected P&L");
            if (!priceItem) return [];
            const price = priceItem.parsed.x;
            const t0Point = nearestByPrice(curve.points, price);
            const t0Index = t0Point ? curve.points.indexOf(t0Point) : -1;
            const delta = t0Index >= 0 ? approximateDeltaAt(curve.points, t0Index) : null;
            return delta != null ? [`Delta exposure (approx.): ${fmtNum(delta, 3)}`] : [];
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: viewWindow.min,
        max: viewWindow.max,
        // Axis title omitted deliberately — the x-axis is unambiguous from
        // the tooltip ("Underlying: ...") and the surrounding page context,
        // and skipping it reclaims the vertical room the Breakeven/strike
        // overlay labels below the axis need (see `layout.padding.bottom`)
        // so they don't collide with either the tick-number row or a title.
        ticks: { callback: (v: number | string) => fmtNum(Number(v), 0) },
      },
      y: {
        title: { display: true, text: "P&L (₹)", font: { size: 11 } },
        ticks: { callback: (v: number | string) => fmtNum(Number(v), 0) },
      },
    },
    // Shrinks the plot area slightly to reserve fixed room for the
    // absolutely-positioned overlay labels: Breakeven labels sit below the
    // x-axis (bottom padding), static (non-Condor) strike labels sit above
    // the plot area (top padding) — see `scheduleGeometryUpdate` below.
    layout: {
      padding: { top: staticStrikeGroups.length > 0 ? 20 : 6, bottom: 24 },
    },
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // ---- Overlay geometry (drag handles + breakeven axis labels) ----------

  const scheduleGeometryUpdate = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const chart = chartRef.current;
      if (!chart) return;
      const xScale = chart.scales.x;
      const { top, bottom } = chart.chartArea;

      if (condor) {
        const roles: Array<[CondorRole, OptionLeg]> = [
          ["LONG_PUT", condor.longPut],
          ["SHORT_PUT", condor.shortPut],
          ["SHORT_CALL", condor.shortCall],
          ["LONG_CALL", condor.longCall],
        ];
        setHandles(
          roles.map(([role, leg]) => ({
            role,
            strike: leg.strike,
            leftPx: xScale.getPixelForValue(leg.strike),
            topPx: top,
            bottomPx: bottom,
          }))
        );
      } else {
        setHandles([]);
      }

      // `thinByPixelGap` drops any label that would land within
      // MIN_LABEL_GAP_PX of the previous one — the exact "labels collapse
      // into an unreadable smear" bug the view-window fix targets can still
      // recur in miniature at extreme zoom (e.g. two breakevens a handful
      // of points apart); the reference line/handle still renders either
      // way, only the redundant text is skipped.
      setBreakevenLabels(
        thinByPixelGap(
          breakevenValues.map((value) => ({
            value,
            leftPx: xScale.getPixelForValue(value),
            topPx: bottom + 20,
          }))
        )
      );

      setStaticStrikeLabels(
        thinByPixelGap(
          staticStrikeGroups.map((group) => ({
            ...group,
            leftPx: xScale.getPixelForValue(group.strike),
            topPx: top - 6,
          }))
        )
      );
    });
  }, [condor, breakevenValues, staticStrikeGroups]);

  useEffect(() => {
    scheduleGeometryUpdate();
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [data, scheduleGeometryUpdate]);

  // ---- Drag interaction ---------------------------------------------------

  const endDrag = useCallback(() => {
    draggingRoleRef.current = null;
    setDraggingRole(null);
    onDragActiveChange?.(false);
  }, [onDragActiveChange]);

  useEffect(() => {
    function handleMouseMove(event: MouseEvent) {
      const role = draggingRoleRef.current;
      const chart = chartRef.current;
      if (!role || !chart) return;
      const currentCondor = identifyCondorLegs(legsRef.current);
      if (!currentCondor) {
        endDrag();
        return;
      }
      const rect = chart.canvas.getBoundingClientRect();
      const canvasX = event.clientX - rect.left;
      const rawValue = chart.scales.x.getValueForPixel(canvasX);
      if (rawValue == null) return;
      const clamped = applyCondorDragGuardrails(currentCondor, role, rawValue, strikeInterval);
      const nextLegs = applyCondorStrikeChange(legsRef.current, currentCondor, role, clamped);
      onChangeLegs(nextLegs);
    }
    function handleMouseUp() {
      if (draggingRoleRef.current !== null) endDrag();
    }
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [strikeInterval, onChangeLegs, endDrag]);

  function startDrag(role: CondorRole) {
    draggingRoleRef.current = role;
    setDraggingRole(role);
    onDragActiveChange?.(true);
  }

  // ---- Wing width controller + What-If spot ------------------------------

  function handleWingWidth(deltaPoints: number) {
    if (!condor) return;
    onChangeLegs(widenWingsSymmetrically(legs, condor, deltaPoints, strikeInterval));
  }

  function handleSpotPreset(pct: number) {
    const base = liveUnderlyingPrice ?? curve.underlying_price;
    if (base == null) return;
    onChangeUnderlyingPriceOverride(Math.round(base * (1 + pct / 100) * 100) / 100);
  }

  const netPremiumLocal = useMemo(() => computeNetPremium(legs), [legs]);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography id={titleId} variant="subtitle2" fontWeight={700}>
          Payoff Visualizer
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color="warning"
          label="Estimated Payoff Curves based on Black-Scholes / Expiry logic. Not a guarantee of filled returns."
        />
      </Stack>

      {!curve.is_single_expiry && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Calendar/diagonal (mixed-expiry) position — an exact at-expiry payoff line can't be drawn the same way
          (the far-dated leg still has time value left), so only the T+0 curve is shown below.
        </Alert>
      )}

      {condor && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }} useFlexGap>
          <Typography variant="caption" color="text.secondary">
            Wing width:
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            <Button onClick={() => handleWingWidth(-100)}>-100</Button>
            <Button onClick={() => handleWingWidth(-50)}>-50</Button>
            <Button onClick={() => handleWingWidth(50)}>+50</Button>
            <Button onClick={() => handleWingWidth(100)}>+100</Button>
          </ButtonGroup>
        </Stack>
      )}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }} useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
          What-if spot:
        </Typography>
        <Box sx={{ width: 220, px: 1 }}>
          <Slider
            size="small"
            min={viewWindow.min}
            max={viewWindow.max}
            step={strikeInterval / 5 || 1}
            value={effectiveSpot ?? (liveUnderlyingPrice ?? viewWindow.min)}
            onChange={(_e, value) => onChangeUnderlyingPriceOverride(Array.isArray(value) ? value[0] : value)}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => fmtNum(v, 0)}
          />
        </Box>
        <ButtonGroup size="small" variant="outlined">
          {SPOT_MOVE_PRESETS.map((pct) => (
            <Button key={pct} onClick={() => handleSpotPreset(pct)}>
              {pct > 0 ? `+${pct}%` : `${pct}%`}
            </Button>
          ))}
        </ButtonGroup>
        {underlyingPriceOverride != null && (
          <MuiTooltip title="Reset to live spot">
            <Button size="small" startIcon={<RestartAltIcon fontSize="small" />} onClick={() => onChangeUnderlyingPriceOverride(null)}>
              Live
            </Button>
          </MuiTooltip>
        )}
      </Stack>

      <Box ref={containerRef} sx={{ position: "relative", height: { xs: 300, sm: 360 } }}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Line key={chartKey} ref={chartRef as any} data={data} options={options} />

        {handles.map((handle) => (
          <MuiTooltip key={handle.role} title={`${CONDOR_ROLE_LABELS[handle.role]}: ${fmtNum(handle.strike, 0)}`}>
            <Box
              onMouseDown={(e) => {
                e.preventDefault();
                startDrag(handle.role);
              }}
              sx={{
                position: "absolute",
                left: handle.leftPx - 5,
                top: handle.topPx,
                width: 10,
                height: handle.bottomPx - handle.topPx,
                cursor: "ew-resize",
                zIndex: 3,
                display: "flex",
                justifyContent: "center",
                "&:hover .payoff-handle-line": { opacity: 1 },
              }}
            >
              <Box
                className="payoff-handle-line"
                sx={{
                  width: 2,
                  height: "100%",
                  bgcolor: COLOR_HANDLE[handle.role],
                  opacity: draggingRole === handle.role ? 1 : 0.55,
                  transition: "opacity 0.1s",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: -6,
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: COLOR_HANDLE[handle.role],
                  border: "2px solid #fff",
                  boxShadow: 1,
                }}
              />
            </Box>
          </MuiTooltip>
        ))}

        {breakevenLabels.map((label) => (
          <Typography
            key={`be-${label.value}`}
            variant="caption"
            sx={{
              position: "absolute",
              left: label.leftPx,
              top: label.topPx,
              transform: "translateX(-50%)",
              color: COLOR_BREAKEVEN,
              fontWeight: 700,
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            BE {fmtNum(label.value, 0)}
          </Typography>
        ))}

        {staticStrikeLabels.map((label) => (
          <MuiTooltip key={`strike-${label.strike}`} title={`${label.label} ${fmtNum(label.strike, 0)}`}>
            <Box
              sx={{
                position: "absolute",
                left: label.leftPx,
                top: label.topPx,
                transform: "translate(-50%, -100%)",
                pointerEvents: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                  bgcolor: label.color,
                  borderRadius: 0.5,
                  px: 0.5,
                  lineHeight: 1.6,
                  whiteSpace: "nowrap",
                }}
              >
                {fmtNum(label.strike, 0)}
              </Typography>
            </Box>
          </MuiTooltip>
        ))}
      </Box>

      {condor && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 3.5, mb: 0.5 }}>
          {CONDOR_ROLE_ORDER.map((role) => (
            <Chip
              key={role}
              size="small"
              variant="outlined"
              label={`${CONDOR_ROLE_LABELS[role]}: ${fmtNum(
                role === "LONG_PUT"
                  ? condor.longPut.strike
                  : role === "SHORT_PUT"
                    ? condor.shortPut.strike
                    : role === "SHORT_CALL"
                      ? condor.shortCall.strike
                      : condor.longCall.strike,
                0
              )}`}
              sx={{ borderColor: COLOR_HANDLE[role] }}
            />
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Solid = at-expiry P&L (live, computed instantly from the Trade Builder's own legs). Dashed = today's
        Black-Scholes theoretical (T+0) P&L — a model estimate, refreshed a moment after you stop editing. The
        chart auto-zooms to the strikes/breakevens/spot that matter for this trade — it does not show the full
        price range the curve is computed over.
        {condor
          ? " Drag a strike marker above to reshape this Iron Condor."
          : staticStrikeGroups.length > 0
            ? " Dotted vertical lines mark each leg's strike (blue = bought, teal = sold, gray = shared by more than one leg)."
            : ""}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Net premium (current legs): {fmtCurrency(netPremiumLocal)}
      </Typography>
      {/* Build fingerprint (see vite.config.ts) -- deliberately small and
         low-contrast, but always present: several rounds of "the axis is
         still wrong" reports turned out to be a stale/undeployed build,
         each only provable via a DevTools round-trip. A screenshot of this
         line settles it immediately going forward. */}
      <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5, fontSize: 10 }}>
        build {__BUILD_SHA__} · {__BUILD_TIME__}
      </Typography>
    </Box>
  );
}

export default PayoffChart;
